import { useContext, useState, useEffect, useRef, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import LayoutPage from "../../../templates/Layout/LayoutPage";
import { Grid, Box, Button, Tooltip, Menu, MenuItem, Select, Accordion, AccordionSummary, AccordionDetails, Alert, ToggleButton, ToggleButtonGroup, Paper } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BoardBuilder from "../../../components/organisms/BoardBuilder/BoardBuilder";
import { yearList, simpleFetch, valueFormatted, buildOnLoad } from "src/utils/helpsers";
import { MenuBoxStyles, BoardActionsStyles, BoardGroupCounter } from "../DistrictInsight/DistrictInsight.styles";
import { HealthBenefitsStyles } from "./HealthBenefits.styles";
import MapViewHealth from "src/components/organisms/MapView/MapViewHealth";
import DistrictCard from "src/components/organisms/DistrictCard/DistrictCard";
import GroupDisplay from "../../../components/molecules/GroupDisplay/GroupDisplay";
import MultiVariableFilter from "../../../components/organisms/MultiVariableFilter/MultiVariableFilter";
import LollipopChart from "src/components/organisms/LollipopChart/LollipopChart";
import DataGridTable from "../../../components/organisms/Table/DataGridTable";
import StackedChart from "../../../components/organisms/StackedChart/StackedChart";
import { TokenContext } from "../../../utils/Context";
import ReportButtons from "../../../components/organisms/BugReport/ReportButtons";

function HealthBenefits() {
    // User session token
    const { userToken } = useContext(TokenContext);

    // User session metadata and data
    const { user } = useAuth0();
    const metadata = user[`${process.env.REACT_APP_AUTH0_KEY}/district`];
    const enabledHealthBenefits = metadata.enable_hbenefits;

    const navigate = useNavigate();

    const options = useMemo(() => [
        { label: 'TSB/% of BA Base Salary Family', value: 'percentage_of_ba_salary_family', valueVar: "percentage_of_ba_salary_family" },
        { label: 'TSB/% of BA Base Salary Single', value: 'percentage_of_ba_salary_single', valueVar: "percentage_of_ba_salary_single" },
        { label: 'TSB/Deductible In Network Single', value: 'deductible_in_network_single', valueVar: "q19a_netdeds_p9q2r1m1c1" },
        { label: 'TSB/Deductible In Network Family', value: 'deductible_in_network_family', valueVar: "q19c_netdedf_p9q2r2m1c1"},
        { label: 'TSB/Total Out of Pocket Family', value: 'total_out_of_pocket_family', valueVar: "q20c_netoopf_p9q3r2m1c1" },
        { label: 'TSB/Total Out of Pocket Single', value: 'total_out_of_pocket_single', valueVar: "q20a_netoops_p9q3r1m1c1" },
    ], []);

    // Academic level data control
    const [toggleType, setToggleType] = useState(true);

    const [referenceDistrict, setReferenceDistrict] = useState();
    const [year, setYear] = useState({});
    const [groupInfo, setGroupInfo] = useState();
    const [groupType, setGroupType] = useState();
    const [irns, setIrns] = useState()
    const [boardData, setBoardData] = useState();
    const [btnFilter, setBtnFilter] = useState(null);
    const [districtsCount, setDistrictsCount] = useState();
    const [mapVariable, setMapVariable] = useState();
    const [healthVariable, setHealthVariable] = useState();
    const [districtInfo, setDistrictInfo] = useState();
    const [mapData, setMapData] = useState();
    const [healthTableData, setHealthTableData] = useState();
    const [updatedTableData, setUpdatedTableData] = useState(null);
    const [selectedPlantype, setSelectedPlantype] = useState("");
    const [selectedPlannum, setSelectedPlannum] = useState('');
    const [toggleOptions, setToggleOptions] = useState([]);
    const [varsFilters, setVarsFilters] = useState(null);

    //Stacked column chart
    const [stackedColumnData, setStackedColumnData] = useState();
    const [nullDistricts, setNullDistricts] = useState([]);
    const [districtDataNull, setDistrictDataNull] = useState(false);
    const [groupDataNull, setGroupDataNull] = useState(false);

    //Lollipop data
    const [lollipopGroup, setLollipopGroup] = useState();
    const [histogramBaseSalary, setHistogramBaseSalary] = useState();
    const [varBaseSalary, setVarBaseSalary] = useState();
    const [statsBaseSalary, setStatsBaseSalary] = useState();
    const [histogramOutPocket, setHistogramOutPocket] = useState();
    const [varOutPocket, setVarOutPocket] = useState();
    const [statsOutPocket, setStatsOutPocket] = useState();
    const [histogramDeductible, setHistogramDeductible] = useState();
    const [varDeductible, setVarDeductible] = useState();
    const [statsDeductible, setStatsDeductible] = useState();
    const [resetFilter, setResetFilter] = useState({}); 

    const openFilters = Boolean(btnFilter);

    const handleFilter = () => {
        setBtnFilter(null);
    };

    const handleChange = (event) => {
        setMapVariable(event.target.value);
    }

    const clickFilters = (event) => {
        setBtnFilter(event.currentTarget);
    };

    const closeFilters = () => {
        setBtnFilter(null);
    };

    // Reset components data
    const handleEmployeeScope = () => {
        setHistogramBaseSalary();
        setHistogramOutPocket();
        setHistogramDeductible();
        setToggleType(!toggleType)
    }

    const handlePlantypeChange = (e) => {
        const selectedValue = toggleOptions.find(option => option.value === e.target.value);
        setSelectedPlantype(selectedValue);
        setSelectedPlannum(selectedValue.plannums[0]);
        setDistrictDataNull(false);
        setGroupDataNull(false);
        setHistogramBaseSalary();
        setHistogramOutPocket();
        setHistogramDeductible();

        //Reset board filters and districtCount after plantype change 
        let irnsSplit = irnsRef.current.split(',')
        let irnsArray = irnsRef.current.replaceAll(' ', '').split(',')
        let count = irnsSplit.length;
        setDistrictsCount(count);
        setIrns(irnsArray);
        setResetFilter(prev => prev + 1) 
    }

    const handlePlannumChange = (e, newValue) => {
        if (newValue !== null) {
            setSelectedPlannum(newValue);
            setDistrictDataNull(false);
            setGroupDataNull(false);
            setHistogramBaseSalary();
            setHistogramOutPocket();
            setHistogramDeductible();

            //Reset board filters and districtCount after plannum change 
            let irnsSplit = irnsRef.current.split(',');
            let irnsArray = irnsRef.current.replaceAll(' ', '').split(',');
            let count = irnsSplit.length;
            setDistrictsCount(count);
            setIrns(irnsArray);
            setResetFilter(prev => prev + 1);
        }
    };

    // Data from the original group, when a filter is applied this data is filtered.
    const irnsRef = useRef();
    const mapRef = useRef();
    const planRef = useRef();
    const plannumRef = useRef();
    const healthTableDataRef = useRef();

    useEffect(() => {
        if (metadata) {
            const userSessionType = metadata.hasOwnProperty("sessionType")
                ? metadata.sessionType
                : "forbidden";
            switch (userSessionType) {
                case "district-access":
                    window.localStorage.setItem("session-type", "district-access");
                    break;
                case "full-access":
                    window.localStorage.setItem("session-type", "full-access");
                    break;
                case "multiple-district-access":
                    window.localStorage.setItem(
                        "session-type",
                        "multiple-district-access"
                    );
                    break;
                case "forbidden":
                    navigate("/AccessDenied", { replace: true });
                    break;
                default:
                    navigate("/AccessDenied", { replace: true });
                    break;
            }
        } else {
            navigate("/AccessDenied", { replace: true });
        }
    }, [navigate, user, metadata]);

    useEffect(() => {
        if (boardData) {
            // Irns into an array to be iterables.
            let irnsToString = (boardData.irns).toString()
            let irnsArray = irnsToString.replaceAll(' ', '').split(',')
            let irnsSplit = irnsToString.split(',')
            let count = irnsSplit.length;

            setReferenceDistrict(boardData.referenceDistrict);
            setYear(boardData.year);
            setIrns(irnsArray)
            setDistrictsCount(count)
            setGroupInfo(boardData.groupInfo);
            setGroupType(boardData.groupType)

            // ---- Cleaning board
            setHealthTableData();
            setUpdatedTableData();
            setMapData();
            setHistogramBaseSalary();
            setHistogramOutPocket();
            setHistogramDeductible();
            setDistrictDataNull(false);
            setGroupDataNull(false);
            setToggleOptions();
            setStackedColumnData();
            setSelectedPlantype();
            setSelectedPlannum();

            irnsRef.current = boardData.irns;
        }
    }, [boardData]);

    useEffect(() => {
        if (userToken) {
            buildOnLoad({ user: user, callback: setBoardData, token: userToken });
        }
    }, [navigate, user, userToken])

    useEffect(() => {
        if (year.value && referenceDistrict.value && enabledHealthBenefits) {
            let endpoint = year.value === "MCD" ? `${process.env.REACT_APP_API_URL}/mcd_health_insurance_plans?irn=eq.${referenceDistrict.value}` : `${process.env.REACT_APP_API_URL}/health_insurance_plans?irn=eq.${referenceDistrict.value}&file_year=eq.${year.value}`
            simpleFetch(endpoint
            ).then((data) => {
                if (data.length > 0) {
                    const planTypeMapping = {
                        1: "EPO",
                        2: "PPO",
                        3: "POS",
                        4: "HMO",
                        5: "HDHP",
                        0: "Other"
                    };
    
                    const plannums = {};
                    const plantypes = [];
                    data[0].plans.forEach(plan => {
                        const num = planTypeMapping[plan.plantype] || 'Other';
                        if (!plannums[num]) {
                            plannums[num] = [];
                        }
                        plannums[num].push(plan.plannum);
                        plantypes[num] = plan.plantype;
                    });
    
                    const options = Object.keys(plannums).map(type => ({
                        value: type,
                        plannums: plannums[type],
                        plantype: plantypes[type]
                    }));
    
                    setToggleOptions(options)
                    const firstType = options[0];
                    setSelectedPlantype(firstType);
                    setSelectedPlannum(firstType.plannums[0]);
                }
            });
        }
    }, [year, referenceDistrict, enabledHealthBenefits])

    useEffect(() => {
        let mode = toggleType ? "single" : "family"
        if (referenceDistrict?.value && year?.value && selectedPlantype && selectedPlannum && enabledHealthBenefits) {
            simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_resume_health_benefits?_plantype=${selectedPlantype.plantype}&_plannum=${selectedPlannum}&_year=${year.value}&_irn=${referenceDistrict.value}`
            ).then(data => {
                const filterProperty = mode === "family" ? "_single" : "_family";
                const filteredStaticData = {};
                const filteredTableData = {};

                Object.entries(data[0].static_data).forEach(([key, value]) => {
                    if (!key.includes(filterProperty)) {
                        let formattedValue = value.value;
                        if (value.value !== null && !value.label.includes("Employee Contribution (% of Premium)") && !value.label.includes("Number of medical plans for a district")) {
                            formattedValue = `$${formattedValue}`;
                        }

                        filteredStaticData[key] = {
                            label: value.label,
                            value: formattedValue
                        };
                    }
                });

                Object.entries(data[0].table_data).forEach(([key, value]) => {
                    if (!key.includes(filterProperty)) {
                        let formattedValue = value.value;
                        if (value.value !== null && !value.label.includes("Employee Contribution") && !value.label.includes("Number of medical plans")) {
                            formattedValue = `$${formattedValue}`;
                        }

                        filteredTableData[key] = {
                            label: value.label,
                            state: formattedValue,
                            rank_in_state: value.rank_in_state,
                            value: formattedValue
                        };
                    }
                });

                const filteredDistrictInfo = {
                    ...data[0],
                    static_data: filteredStaticData,
                    table_data: filteredTableData
                };

                setDistrictInfo(filteredDistrictInfo);
            });
        }
    }, [referenceDistrict, year, toggleType, selectedPlantype, selectedPlannum, enabledHealthBenefits]);

    useEffect(() => {
        if (year.value && referenceDistrict.value && irns && selectedPlantype && selectedPlannum && enabledHealthBenefits) {
            let irnsUpdated = [...irns, referenceDistrict.value]
            simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_hb_data_graphs?_irn=${referenceDistrict.value}&_year=${year.value}&_irns=${irnsUpdated}&_plantype=${selectedPlantype.plantype}&_plannum=${selectedPlannum}`)
                .then(data => {
                    const employeePremiumData = {
                        name: "Employee",
                        data: []
                    };

                    const districtPremiumData = {
                        name: "District",
                        data: []
                    };

                    const categories = [];
                    const allNullDistricts = [];
                    data.forEach(item => {
                        if (!item.has_data) {
                            if (item.irn === referenceDistrict.value) {
                                setDistrictDataNull(true)
                            }
                            allNullDistricts.push({
                                name: item.district_name
                            })
                            return;
                        }
                        const districtName = item.district_name;
                        let employeePremiumContribution = toggleType ? item.single.employee_premium_contribution_single : item.family.employee_premium_contribution_family;
                        let districtPremiumContribution = toggleType ? item.single.district_premium_contribution_single : item.family.district_premium_contribution_family;
                        if (item.irn === referenceDistrict.value) {
                            setDistrictDataNull(false)
                            districtPremiumContribution = {
                                y: districtPremiumContribution,
                                color: "#F5B335"
                              };
                        }

                        categories.push(districtName);
                        employeePremiumData.data.push(employeePremiumContribution);
                        districtPremiumData.data.push(districtPremiumContribution);
                    });

                    const result = {
                        series: [districtPremiumData, employeePremiumData],
                        categories: categories
                    };
                    setStackedColumnData(result)
                    setNullDistricts(allNullDistricts)
                })
        }
    }, [year, referenceDistrict, irns, toggleType, selectedPlantype, selectedPlannum, enabledHealthBenefits])

    useEffect(() => {
        if (stackedColumnData && !stackedColumnData.series[0].data.length) {
            setGroupDataNull(!stackedColumnData.series[0].data.length)
        }
    }, [stackedColumnData])

    useEffect(() => {
        //let year_value = year.value ? year.value : 2020;

        if (year.value === 'MCD' && enabledHealthBenefits) {
            simpleFetch(`${process.env.REACT_APP_API_URL}/mcd_sv_health?visible=eq.true&order=label.asc&erirn_employer_ein=eq.45187&plannum=eq.1&q13_plantype_p7q3=eq.5&dwh_variable=in.("percentage_of_ba_salary_family","percentage_of_ba_salary_single","q20c_netoopf_p9q3r2m1c1","q20a_netoops_p9q3r1m1c1","q19c_netdedf_p9q2r2m1c1","q19a_netdeds_p9q2r1m1c1")&select=label,description,dwh_variable`
            ).then((data) => {
                setLollipopGroup(data);
            });
        } else {
            simpleFetch(`${process.env.REACT_APP_API_URL}/hvd_source_variable?file_year=eq.2021&visible=eq.true&order=label.asc&value_var=in.("percentage_of_ba_salary_family","percentage_of_ba_salary_single","q20c_netoopf_p9q3r2m1c1","q20a_netoops_p9q3r1m1c1","q19c_netdedf_p9q2r2m1c1","q19a_netdeds_p9q2r1m1c1")`
            ).then((data) => {
                setLollipopGroup(data);
            });
        }
    }, [year, enabledHealthBenefits]);

    //Histogram Base Salary
    useEffect(() => {
        let histogramSalaryData = { categories: [], data: [], district: {} };
        let levelVariable = toggleType ? "percentage_of_ba_salary_single" : "percentage_of_ba_salary_family";

        if (irns && lollipopGroup && referenceDistrict && selectedPlantype && selectedPlannum) {
            let updatedIrns = [...irns, referenceDistrict.value]

            let value = year.value === "MCD" ? "dwh_variable" : "value_var"
            let varAcc = lollipopGroup.filter(function (variable) { return variable[value] === levelVariable })[0];

            setVarBaseSalary(varAcc);

            // If not irns the stats are not required
            if (irns.length && enabledHealthBenefits) {
                simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_stats_health?_plantype=${selectedPlantype.plantype}&_plannum=${selectedPlannum}&_var=${levelVariable}&_irn=${referenceDistrict.value}&_irns=${irns}&_year=${year.value}`
                ).then((data) =>
                    setTimeout(() => {
                        setStatsBaseSalary([data[0].min_var, data[0].max_var]);
                    }, 10)
                );
            }

            if (year.value === 'MCD' && enabledHealthBenefits) {
                simpleFetch(`${process.env.REACT_APP_API_URL}/mcd_hb_tabloide?select=${levelVariable},${levelVariable}_yr,district_name, irn&irn=in.(${updatedIrns})&plannum=eq.${selectedPlannum}&q13_plantype_p7q3=eq.${selectedPlantype.plantype}`
                ).then((data) =>
                    data.forEach((item) => {
                        let distinct_year = item[`${levelVariable}_yr`] === yearList[1].value ? '' : ` (${item[`${levelVariable}_yr`]})`;

                        histogramSalaryData["categories"].push(`${item.district_name}${distinct_year}`);
                        if (item.irn === referenceDistrict.value) {
                            histogramSalaryData["data"].push({
                                y: item[levelVariable],
                                selected: true,
                                color: "#F5B335",
                            });
                            histogramSalaryData["district"] = item;
                        } else {
                            histogramSalaryData["data"].push(item[levelVariable]);
                        }
                    })
                )
                    .then(() => setHistogramBaseSalary(histogramSalaryData))
                    .catch((e) => setHistogramBaseSalary("no-data"));
            } else if (enabledHealthBenefits) {
                simpleFetch(`${process.env.REACT_APP_API_URL}/health_insurance_readable?select=${levelVariable},district_name,irn&irn=in.(${updatedIrns})&plannum=eq.${selectedPlannum}&q13_plantype_p7q3=eq.${selectedPlantype.plantype}&file_year=eq.${year.value}&${levelVariable}=not.is.null&order=${levelVariable}.desc`
                ).then((data) => {
                    if (data?.length) {
                        data.forEach((item) => {
                            histogramSalaryData["categories"].push(item.district_name);
                            if (item.irn === referenceDistrict.value) {
                                histogramSalaryData["data"].push({
                                    y: item[levelVariable],
                                    selected: true,
                                    color: "#F5B335",
                                });
                                histogramSalaryData["district"] = item;
                            } else {
                                histogramSalaryData["data"].push(item[levelVariable]);
                            }
                        })
                        setHistogramBaseSalary(histogramSalaryData)
                    } else {
                        // On no data, set empty array to display an empty chart
                        setHistogramBaseSalary([])
                    }
                })
                    .catch(() => setHistogramBaseSalary("no-data"));
            }
        }
    }, [irns, referenceDistrict, year, lollipopGroup, toggleType, selectedPlantype, selectedPlannum, enabledHealthBenefits]);

    //Histogram Total Out of Pocket
    useEffect(() => {
        let histogramOP = { categories: [], data: [], district: {} };
        let levelVariable = toggleType ? "q19a_netdeds_p9q2r1m1c1" : "q19c_netdedf_p9q2r2m1c1";;

        if (irns && lollipopGroup && referenceDistrict && selectedPlantype && selectedPlannum) {
            let updatedIrns = [...irns, referenceDistrict.value]

            let value = year.value === "MCD" ? "dwh_variable" : "value_var"
            let varAcc = lollipopGroup.filter(function (variable) { return variable[value] === levelVariable })[0];
            setVarOutPocket(varAcc);

            // If not irns the stats are not required
            if (irns.length) {
                simpleFetch(
                    `${process.env.REACT_APP_API_URL}/rpc/hvd_stats_health?_plantype=${selectedPlantype.plantype}&_plannum=${selectedPlannum}&_var=${levelVariable}&_irn=${referenceDistrict.value}&_irns=${irns}&_year=${year.value}`
                ).then((data) =>
                    setTimeout(() => {
                        setStatsOutPocket([data[0].min_var, data[0].max_var]);
                    }, 10)
                );
            }

            if (year.value === 'MCD' && enabledHealthBenefits) {
                simpleFetch(
                    `${process.env.REACT_APP_API_URL}/mcd_hb_tabloide?select=${levelVariable},${levelVariable}_yr,district_name,irn&irn=in.(${updatedIrns})&plannum=eq.${selectedPlannum}&q13_plantype_p7q3=eq.${selectedPlantype.plantype}`
                )
                    .then((data) =>
                        data.forEach((item) => {
                            let distinct_year = item[`${levelVariable}_yr`] === yearList[1].value ? '' : ` (${item[`${levelVariable}_yr`]})`;

                            histogramOP["categories"].push(`${item.district_name}${distinct_year}`);
                            if (item.irn === referenceDistrict.value) {
                                histogramOP["data"].push({
                                    y: item[levelVariable],
                                    selected: true,
                                    color: "#F5B335",
                                });
                                histogramOP["district"] = item;
                            } else {
                                histogramOP["data"].push(item[levelVariable]);
                            }
                        })
                    )
                    .then(() => setHistogramOutPocket(histogramOP))
                    .catch((e) => setHistogramOutPocket("no-data"));
            } else if (enabledHealthBenefits) {
                simpleFetch(
                    `${process.env.REACT_APP_API_URL}/health_insurance_readable?select=${levelVariable},district_name,irn&irn=in.(${updatedIrns})&plannum=eq.${selectedPlannum}&q13_plantype_p7q3=eq.${selectedPlantype.plantype}&file_year=eq.${year.value}&${levelVariable}=not.is.null&order=${levelVariable}.desc`
                )
                    .then((data) => {
                        if (data?.length) {
                            data.forEach((item) => {
                                histogramOP["categories"].push(item.district_name);
                                if (item.irn === referenceDistrict.value) {
                                    histogramOP["data"].push({
                                        y: item[levelVariable],
                                        selected: true,
                                        color: "#F5B335",
                                    });
                                    histogramOP["district"] = item;
                                } else {
                                    histogramOP["data"].push(item[levelVariable]);
                                }
                            })
                            setHistogramOutPocket(histogramOP)
                        } else {
                            // On no data, set empty array to display an empty chart
                            setHistogramOutPocket([])
                        }
                    })
                    .catch(() => setHistogramOutPocket("no-data"));
            }
        }
    }, [irns, referenceDistrict, year, lollipopGroup, toggleType, selectedPlannum, selectedPlantype, enabledHealthBenefits]);

    //Histogram Deductible in network
    useEffect(() => {
        let histogramDeductible = { categories: [], data: [], district: {} };
        let levelVariable = toggleType ? "q20a_netoops_p9q3r1m1c1" : "q20c_netoopf_p9q3r2m1c1";

        if (irns && lollipopGroup && referenceDistrict && selectedPlantype && selectedPlannum) {
            let updatedIrns = [...irns, referenceDistrict.value]

            let value = year.value === "MCD" ? "dwh_variable" : "value_var"
            let varAcc = lollipopGroup.filter(function (variable) { return variable[value] === levelVariable })[0];
            setVarDeductible(varAcc);

            // If not irns the stats are not required
            if (irns.length) {
                simpleFetch(
                    `${process.env.REACT_APP_API_URL}/rpc/hvd_stats_health?_plantype=${selectedPlantype.plantype}&_plannum=${selectedPlannum}&_var=${levelVariable}&_irn=${referenceDistrict.value}&_irns=${irns}&_year=${year.value}`
                ).then((data) =>
                    setTimeout(() => {
                        setStatsDeductible([data[0].min_var, data[0].max_var]);
                    }, 10)
                );
            }

            if (year.value === 'MCD' && enabledHealthBenefits) {
                simpleFetch(
                    `${process.env.REACT_APP_API_URL}/mcd_hb_tabloide?select=${levelVariable},${levelVariable}_yr,district_name,irn&irn=in.(${updatedIrns})&plannum=eq.${selectedPlannum}&q13_plantype_p7q3=eq.${selectedPlantype.plantype}`
                )
                    .then((data) =>
                        data.forEach((item) => {
                            let distinct_year = item[`${levelVariable}_yr`] === yearList[1].value ? '' : ` (${item[`${levelVariable}_yr`]})`;

                            histogramDeductible["categories"].push(`${item.district_name}${distinct_year}`);
                            if (item.irn === referenceDistrict.value) {
                                histogramDeductible["data"].push({
                                    y: item[levelVariable],
                                    selected: true,
                                    color: "#F5B335",
                                });
                                histogramDeductible["district"] = item;
                            } else {
                                histogramDeductible["data"].push(item[levelVariable]);
                            }
                        })
                    )
                    .then(() => setHistogramDeductible(histogramDeductible))
                    .catch((e) => setHistogramDeductible("no-data"));
            } else if (enabledHealthBenefits) {
                simpleFetch(
                    `${process.env.REACT_APP_API_URL}/health_insurance_readable?select=${levelVariable},district_name,irn&irn=in.(${updatedIrns})&plannum=eq.${selectedPlannum}&q13_plantype_p7q3=eq.${selectedPlantype.plantype}&file_year=eq.${year.value}&${levelVariable}=not.is.null&order=${levelVariable}.desc`
                )
                    .then((data) => {
                        if (data?.length) {
                            data.forEach((item) => {
                                histogramDeductible["categories"].push(item.district_name);
                                if (item.irn === referenceDistrict.value) {
                                    histogramDeductible["data"].push({
                                        y: item[levelVariable],
                                        selected: true,
                                        color: "#F5B335",
                                    });
                                    histogramDeductible["district"] = item;
                                } else {
                                    histogramDeductible["data"].push(item[levelVariable]);
                                }
                            })
                            setHistogramDeductible(histogramDeductible)
                        } else {
                            // On no data, set empty array to display an empty chart
                            setHistogramDeductible([])
                        }
                    })
                    .catch(() => setHistogramDeductible("no-data"));
            }
        }
    }, [irns, referenceDistrict, year, lollipopGroup, toggleType, selectedPlannum, selectedPlantype, enabledHealthBenefits]);

    /**
     *  When a board is build, map is loaded using a default filter.
     */
    useEffect(() => {
        if (referenceDistrict && year && irnsRef.current && selectedPlantype && selectedPlannum) {
            let query = `${referenceDistrict.value}&_var1=attendance_rate_y1&_min_var1=68.7&_max_var1=99.7&_var2='NA'&_min_var2='NA'&_max_var2='NA'&_year=${year.value}`;
            simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_mapfh?_reference=${query}&_irns=${irnsRef.current}&_plantype=${selectedPlantype.plantype}&_plannum=${selectedPlannum}`)
                .then(data => {
                    setMapData(data);
                    mapRef.current = data;
                })
                planRef.current = selectedPlantype;
                plannumRef.current = selectedPlannum;
        }
    }, [referenceDistrict, year, selectedPlantype, selectedPlannum]);

    useEffect(() => {
        if (referenceDistrict?.value && year?.value && irns && irns.length > 0 && selectedPlantype && selectedPlannum && enabledHealthBenefits) {
            simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_health_schedule_table?_plantype=${selectedPlantype.plantype}&_plannum=${selectedPlannum}&_year=${year.value}&_irns=${irns}`)
                .then(data => {
                    let formattedData = [];
                    data.forEach((district) => {
                        let rowData = {
                            id: district.irn,
                            district_name: district.district_name,
                        };

                        Object.entries(district.data).forEach(([key, value]) => {
                            const fieldName = value.label.replace(/\s+/g, '_');
                            const fieldNameYr = fieldName + '_yr';
                            rowData[fieldName] = value.value;
                            rowData[fieldNameYr] = value.year;
                        });

                        formattedData.push(rowData);
                    });

                    let columns = [
                        {
                            field: 'district_name',
                            headerName: 'District',
                            minWidth: 150,
                            flex: 1,
                        },
                    ];

                    let uniqueLabels = new Set();

                    data.forEach((district) => {
                        Object.keys(district.data).forEach((key) => {
                            const label = district.data[key].label;

                            if (toggleType === true) {
                                if (!label.includes("Family")) {
                                    uniqueLabels.add(label);
                                }
                            } else {
                                if (!label.includes("Single")) {
                                    uniqueLabels.add(label);
                                }
                            }
                        });
                    });

                    uniqueLabels.forEach((label) => {
                        const fieldName = label.replace(/\s+/g, '_');
                        let column = {
                            field: fieldName,
                            headerName: label,
                            minWidth: 150,
                            flex: 1,
                            valueFormatter: (params) => {
                                if (typeof params.value === 'number') {
                                    return valueFormatted(params.value, '');
                                } else if (params.value === null) {
                                    return 'null';
                                }
                            },
                        };
                        columns.push(column);
                        if (year.value === "MCD") {
                            columns.push({
                                field: `${column.field}_yr`,
                                headerName: `${column.headerName} (Year)`,
                                valueFormatter: (params) => {
                                    if (params.value === null) {
                                        return 'null';
                                    }
                                },
                            })
                            return columns
                        }
                    });
                    if (updatedTableData) {
                        const [var1, var2] = updatedTableData.columns;
                        columns.push(var1);

                        if (var2) {
                            columns.push(var2)
                        }

                        formattedData.forEach((row) => {
                            const updatedRow = updatedTableData.rows.find((updatedRow) => updatedRow.id === row.id);
                            if (updatedRow) {
                                formattedData[formattedData.indexOf(row)] = {
                                    ...row,
                                    ...updatedRow
                                };
                            }
                        });
                    }
                    setHealthTableData({ columns: columns, rows: formattedData })
                    healthTableDataRef.current = { columns: columns, rows: formattedData }
                })
        }
    }, [referenceDistrict, year, irns, updatedTableData, toggleType, selectedPlantype, selectedPlannum, enabledHealthBenefits])

    useEffect(() => {
        if (irns && !irns.length > 0 && healthTableDataRef) {
            const previousColumns = healthTableDataRef.current.columns;
            setHealthTableData({columns: previousColumns, rows: []})
        }
    }, [irns]);
    
    useEffect(() => {
        const familyOptions = options.filter(option => !option.value.endsWith('_single'));
        const singleOptions = options.filter(option => !option.value.endsWith('_family'));

        if (toggleType === true) {
            setHealthVariable(singleOptions);
            setMapVariable(singleOptions[0].valueVar)
        } else {
            setHealthVariable(familyOptions);
            setMapVariable(familyOptions[0].valueVar)
        }
    }, [toggleType, options]);
    
    const getFiltersInformation = useRef((data) => {
        let filtersData = data.filtersData.current;

        let var1 = filtersData.vars.var1;
        let var2 = filtersData.vars.var2;

        setVarsFilters(filtersData.vars)

        let lastFilter = Object.keys(filtersData.vars).pop();
        let irnsUpdated = filtersData.vars[lastFilter].irns;
        let mapIrns = !irnsUpdated.length ? filtersData.referenceDistrict.value : irnsUpdated

        let query = `${filtersData.referenceDistrict.value}&_var1=${var1.value_var}&_min_var1=${var1.min_range}&_max_var1=${var1.max_range}&_var2=${var2 ? var2.value_var : "NA"}&_min_var2=${var2 ? var2.min_range : "NA"}&_max_var2=${var2 ? var2.max_range : "NA"}&_year=${filtersData.year.value}`;
        simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_mapfh?_reference=${query}&_irns=${mapIrns}&_plantype=${planRef.current.plantype}&_plannum=${plannumRef.current}`)
            .then(data => {
                setMapData(data);
            })

        let vars = Object.keys(filtersData.vars).map(key => (filtersData.vars[key]['varName'] || filtersData.vars[key]['value_var']));
        let tabloideVars = vars.filter(variable => variable.split('__').length === 1);
        setIrns(irnsUpdated);

        setDistrictsCount(filtersData.group_length)

        let currentBoardDataRows = [];
        let endPoint = filtersData.year.value === 'MCD'
            ? `${process.env.REACT_APP_API_URL}/mcd_tabloide?irn=in.(${irnsUpdated})`
            : `${process.env.REACT_APP_API_URL}/hvd_tabloide?select=irn,district_name,${tabloideVars}&irn=in.(${irnsUpdated})&file_year=eq.${filtersData.year.value}`

        simpleFetch(endPoint)
            .then(data => {
                let newRows = data.map((obj, key) => ({ ...obj, id: obj.irn }));
                let updatedRows = newRows.map(newRow => {
                    let oldRow = currentBoardDataRows.find(oldRow => oldRow.id === newRow.id);
                    return oldRow ? { ...newRow, ...oldRow } : { ...newRow };
                });
                currentBoardDataRows = [...updatedRows];

                let columns = [
                    ...Object.keys(filtersData.vars).map(key => {
                        let fieldBase = filtersData.vars[key];

                        return {
                            field: fieldBase['value_var'] || fieldBase['varName'],
                            headerName: fieldBase['label'],
                            minWidth: 150,
                            flex: 1,
                            valueFormatter: params => {
                                if (typeof params.value === 'number') {
                                    return valueFormatted(params.value, '');
                                }
                            },
                        };
                    }),
                ]
                setUpdatedTableData({
                    columns: columns,
                    rows: currentBoardDataRows
                });
            });
    })

    const topContentElement = (
        <Box className={"board-actions"} sx={BoardActionsStyles}>
            <Grid container className={"board-actions__wrapper"} spacing={2}>
                <Grid item xs={12} lg={3} className={"board-actions__board-menu"} display="flex">
                    <Tooltip title="Filters">
                        <Button
                            id="button-filters"
                            aria-controls={"menu-filters"}
                            aria-haspopup="true"
                            aria-expanded={openFilters ? "true" : "false"}
                            variant="contained"
                            onClick={clickFilters}
                            color={"grape"}
                            className="board-menu__filters"
                        >
                            <FilterAltOutlinedIcon />
                            Filters
                        </Button>
                    </Tooltip>
                    <Menu
                        id="menuFilters"
                        MenuListProps={{
                            "aria-labelledby": "button-filters",
                        }}
                        anchorEl={btnFilter}
                        open={openFilters}
                        onClose={closeFilters}
                        sx={MenuBoxStyles}
                    >
                        <MenuItem disableRipple>
                            <BoardBuilder
                                groupEnabled={true}
                                yearEnabled={true}
                                boardData={setBoardData}
                                onSubmit={() => handleFilter()}
                            />
                        </MenuItem>
                    </Menu>
                </Grid>
                {(groupInfo && districtsCount !== null) && (
                    <Grid item xs={12} lg={9} className="group-counter" sx={BoardGroupCounter}>
                        <div className="group-counter__wrapper">
                            <div className="group-counter__versus">{`${referenceDistrict.label} - vs -`} <GroupDisplay groupData={{ groupInfo: groupInfo, groupType: groupType }} /></div>
                            <div className="group-counter__result">{`Year: ${year.value === "MCD"
                                ? `2026 (${year.label})`
                                : year.label
                                } | ${districtsCount} districts in the comparison group.`}</div>
                        </div>
                    </Grid>
                )}
            </Grid>
        </Box>
    );

    return (
        <LayoutPage
            pageTitle="Benefits Toolkit"
            description="Benefits Toolkit"
            type="dashboard"
            customClass="benefits_toolkit"
            topContent={topContentElement}
        >
            <Box sx={HealthBenefitsStyles}>
                <Grid container spacing={2}>
                    {
                        boardData &&
                        <>
                            <Grid item xs={12}>
                                <Paper className="toggle-container">
                                    <div className="toggle-group">
                                        <span>Employee Scope:</span>
                                        <ToggleButtonGroup
                                            color="primary"
                                            value={toggleType ? 'Single' : 'Family'}
                                            exclusive
                                            onChange={handleEmployeeScope}
                                            aria-label="Platform"
                                        >
                                            <ToggleButton value="Single">Single</ToggleButton>
                                            <ToggleButton value="Family">Family</ToggleButton>
                                        </ToggleButtonGroup>
                                        <div>{toggleType}</div>
                                    </div>
                                    {(toggleOptions && selectedPlantype) &&
                                        <div className="toggle-group">
                                            <span>Plan Type:</span>
                                            <ToggleButtonGroup
                                                color="primary"
                                                value={selectedPlantype.value}
                                                onChange={handlePlantypeChange}
                                                aria-label="Platform"
                                                exclusive
                                                >
                                                {toggleOptions.map(label => (
                                                    <ToggleButton key={label.plantype} value={label.value}>{label.value}</ToggleButton>
                                                ))}
                                            </ToggleButtonGroup>
                                        </div>
                                    }
                                    {(selectedPlannum && selectedPlantype.plannums && selectedPlantype.plannums.length > 1) &&
                                        <div className="toggle-group">
                                            <span>Plan number:</span>
                                            <ToggleButtonGroup
                                                color="primary"
                                                value={selectedPlannum}
                                                onChange={handlePlannumChange}
                                                aria-label="Platform"
                                                exclusive
                                            >
                                                {selectedPlantype.plannums.map(num => (
                                                    <ToggleButton key={num} value={num}>{num}</ToggleButton>
                                                ))}
                                            </ToggleButtonGroup>
                                        </div>
                                    }
                                </Paper>
                            </Grid>
                        </>
                    }
                    {districtInfo &&
                        <Grid item xs={12} lg={6}>
                            <DistrictCard data={districtInfo} />
                        </Grid>
                    }
                    {(referenceDistrict && year) && (
                        <>
                            <Grid item xs={12} lg={6}>
                                <Select
                                    value={mapVariable}
                                    onChange={handleChange}
                                    sx={{ width: "100%" }}
                                >
                                    {healthVariable.map(option => (
                                        <MenuItem key={option.valueVar} value={option.valueVar}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                { (mapData && referenceDistrict?.value) &&
                                    <MapViewHealth data={mapData} districtIrn={referenceDistrict.value} height="520px" zoom={7} choroplethVar={mapVariable} employeeScope={toggleType ? "single" : "family"} />
                                }
                            </Grid>
                            {
                                (districtDataNull || groupDataNull || !districtsCount) &&
                                <Grid item xs={12}>
                                    <Alert severity="warning">
                                        No data available for
                                        {
                                            districtDataNull &&
                                            <strong> {referenceDistrict.label} </strong>
                                        }
                                        {
                                            (districtDataNull && groupDataNull) && ' and '
                                        }
                                        {
                                            (groupDataNull && districtsCount) &&
                                            <strong> {groupInfo.length > 1 ? ' any district in the comparison' : groupInfo[0]?.label} group </strong>
                                        }
                                        {
                                            !districtsCount &&
                                            <strong> any district in the group with the filter(s) applied </strong>
                                        }
                                        in this period.
                                    </Alert>
                                </Grid>
                            }
                            {
                                (stackedColumnData && stackedColumnData.series[0].data.length < 100) &&
                                <Grid item xs={12}>
                                    <StackedChart data={stackedColumnData} />
                                    {
                                        nullDistricts.length > 0 &&
                                        <div className='nulls-block'>
                                            <Accordion defaultExpanded={true} sx={{ background: "#f5f5f5" }}>
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon />}
                                                    aria-controls="panel1a-content"
                                                    id="panel1a-header"
                                                >
                                                    <div className='nulls-block__message'>No data available:</div>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <ul className='nulls-block__districts'>
                                                        {
                                                            nullDistricts.map((district, key) => <li key={key} className='null-district'> {district.name}</li>)
                                                        }
                                                    </ul>
                                                </AccordionDetails>
                                            </Accordion>
                                        </div>
                                    }
                                </Grid>
                            }
                            { // Axis Base Salary lollipop chart.
                                (histogramBaseSalary && varBaseSalary) &&
                                <Grid item xs={12} lg={4} className="lollipop_section">
                                    <LollipopChart
                                        title={varBaseSalary.label}
                                        data={histogramBaseSalary}
                                        description={varBaseSalary.description}
                                        id={"histogram-base-salary"}
                                        ranges={statsBaseSalary}
                                    />
                                </Grid>
                            }
                            { // Axis Total Out of Pocket lollipop chart.
                                (varOutPocket && histogramOutPocket) &&
                                <Grid item xs={12} lg={4} className="lollipop_section">
                                    <LollipopChart
                                        title={varOutPocket.label}
                                        data={histogramOutPocket}
                                        description={varOutPocket.description}
                                        id={"histogram-total-out-of-pocket"}
                                        ranges={statsOutPocket}
                                    />
                                </Grid>
                            }
                            { // Axis Deductible in Network lollipop chart.
                                (varDeductible && histogramDeductible) &&
                                <Grid item xs={12} lg={4} className="lollipop_section">
                                    <LollipopChart
                                        title={varDeductible.label}
                                        data={histogramDeductible}
                                        description={varDeductible.description}
                                        id={"histogram-deductible-network"}
                                        ranges={statsDeductible}
                                    />
                                </Grid>
                            }
                            <Grid className="custom-groups__filters" item xs={12}>
                                <MultiVariableFilter
                                    year={year}
                                    key={resetFilter}
                                    referenceDistrict={referenceDistrict}
                                    disabled={false}
                                    onApply={getFiltersInformation.current}
                                    irns={irnsRef.current}
                                    limit={2}
                                    withMap={false}
                                    returnMapData={true}
                                    withHealthVariables={false}
                                />
                            </Grid>
                            {healthTableData &&
                                <Grid item xs={12}>
                                    <DataGridTable columns={healthTableData.columns} rows={healthTableData.rows} trim={false} />
                                </Grid>
                            }
                        </>
                    ) 
                    }
                </Grid>
            </Box>
            <ReportButtons
                boardType={"Board Benefits Toolkit"}
                collectedData={{
                    referenceDistrict: referenceDistrict,
                    irns: irns,
                    groupInfo: groupInfo,
                    groupType: groupType,
                    groupCount: districtsCount,
                    year: { value: year.value, label: year.value },
                    vars: varsFilters,
                }}
            />
        </LayoutPage>
    );
}

export default HealthBenefits;